"use client";

import { ComponentProps, PropsWithChildren } from "react";
import { Heading as RadixHeading, Slot } from "@radix-ui/themes";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";

interface Props {
  sharedClasses?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
}

export default function Heading({
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  as,
  asChild,
  children,
  ref,
  ...restProps
}: ComponentProps<typeof RadixHeading> & PropsWithChildren<Props>) {
  const isShopsite = useIsShopsite();

  const shopsiteClassName = joinClassNames(
    className,
    sharedClasses,
    shopsiteClasses
  );

  if (isShopsite) {
    if (asChild) {
      return (
        <Slot ref={ref} className={shopsiteClassName} {...restProps}>
          {children}
        </Slot>
      );
    }

    switch (as) {
      case "h1":
        return (
          <h1 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h1>
        );
      case "h2":
        return (
          <h2 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h2>
        );
      case "h3":
        return (
          <h3 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h3>
        );
      case "h4":
        return (
          <h4 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h4>
        );
      case "h5":
        return (
          <h5 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h5>
        );
      case "h6":
        return (
          <h6 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h6>
        );
      default:
        return (
          <h1 ref={ref} className={shopsiteClassName} {...restProps}>
            {children}
          </h1>
        );
    }
  }

  return (
    <RadixHeading
      ref={ref}
      as={as}
      asChild={asChild}
      {...restProps}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixHeading>
  );
}
